import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import * as auth from '@/lib/auth';
import { login as apiLogin, register as apiRegister, refreshToken as apiRefresh, set401Handler } from '@/lib/api';

// Token refresh interval in milliseconds (4 minutes)
const TOKEN_REFRESH_INTERVAL = 4 * 60 * 1000;

interface AuthContextType {
  user: auth.AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Navigation helper (avoids useNavigate hook issues)
function navigateTo(path: string) {
  window.location.href = path;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<auth.AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Logout function (defined before first useEffect)
  const logout = useCallback(() => {
    auth.clearAuth();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
    });
    navigateTo('/login');
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initialState = auth.getInitialAuthState();
    setState(initialState);
    setIsLoading(false);

    // Set up 401 handler
    set401Handler(() => {
      logout();
    });
  }, [logout]);

  // Helper function to update user state (reduces duplication)
  const updateUserState = useCallback((response: { token: string; owner: { id: string; name: string; email: string }; tenantId: string }) => {
    const userState = {
      id: response.owner.id,
      name: response.owner.name,
      email: response.owner.email,
      tenantId: response.tenantId,
    };

    // Store in localStorage
    auth.setToken(response.token);
    auth.setUser(userState);

    // Update React state
    setState({
      user: userState,
      token: response.token,
      isAuthenticated: true,
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await apiLogin({ email, password });
      updateUserState(response);
      navigateTo('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      throw error; // Re-throw to let caller handle it
    }
  }, [updateUserState]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    try {
      const response = await apiRegister({ name, email, password });
      updateUserState(response);
      navigateTo('/dashboard');
    } catch (error) {
      console.error('Registration failed:', error);
      throw error; // Re-throw to let caller handle it
    }
  }, [updateUserState]);

  // Token refresh interval
  useEffect(() => {
    if (!state.token) return;

    let isMounted = true;

    const interval = setInterval(async () => {
      try {
        const { token } = await apiRefresh();

        // Only update state if component is still mounted
        if (isMounted) {
          auth.setToken(token);
          setState(prev => ({ ...prev, token }));
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Only logout if component is still mounted
        if (isMounted) {
          logout();
        }
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [state.token, logout]);

  return (
    <AuthContext.Provider value={{ ...state, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
